import React from "react";
import ServiceCtaButton from "./ServiceCtaButton";
import { NAMED_ICONS } from "./serviceIcons";

const renderParagraphs = (items, keyPrefix, className) =>
  items.map((paragraph, index) =>
    typeof paragraph === "string" ? (
      <p key={`${keyPrefix}-${index}`} className={className}>
        {paragraph}
      </p>
    ) : (
      <React.Fragment key={`${keyPrefix}-${index}`}>{paragraph}</React.Fragment>
    ),
  );

const ContentList = ({ list, listStyle }) => {
  if (!Array.isArray(list) || !list.length) return null;

  if (listStyle === "tags") {
    return (
      <ul className="flex flex-wrap gap-2 mb-4 list-none p-0">
        {list.map((entry, index) => {
          const text = typeof entry === "string" ? entry : entry.text;
          return (
            <li
              key={text || index}
              className="text-sm font-medium text-white px-3 py-1 rounded-full"
              style={{ backgroundColor: "#232528" }}
            >
              {text}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <ul className="space-y-3 mb-4 list-disc list-inside marker:text-[#db0058]">
      {list.map((entry, index) => {
        const label = typeof entry === "string" ? null : entry.label;
        const text = typeof entry === "string" ? entry : entry.text;
        return (
          <li
            key={label || index}
            className="text-gray-700 leading-relaxed text-base sm:text-lg"
          >
            {label && <strong className="text-gray-900">{label}</strong>}
            {label ? " – " : ""}
            {text}
          </li>
        );
      })}
    </ul>
  );
};

const ServiceContentSection = ({
  id,
  title,
  titleDivider,
  content,
  list,
  listStyle = "bullets",
  afterContent,
  highlight,
  image,
  imageAlt,
  imagePosition = "right",
  cta,
  variant = "default",
  badge,
  icon,
  stat,
  tone = "dark",
}) => {
  const hasImage = Boolean(image);
  const HighlightIcon = icon && NAMED_ICONS[icon];
  const paragraphs = Array.isArray(content)
    ? content
    : content
      ? [content]
      : [];
  const afterParagraphs = Array.isArray(afterContent)
    ? afterContent
    : afterContent
      ? [afterContent]
      : [];

  if (variant === "feature") {
    const isLight = tone === "light";
    return (
      <div
        id={id}
        className="scroll-mt-24 rounded-lg p-6 sm:p-8 flex flex-col sm:flex-row gap-6 sm:gap-8 items-start"
        style={
          isLight
            ? { backgroundColor: "#fce7f0", border: "1.5px solid #db0058" }
            : { backgroundColor: "#232528" }
        }
      >
        {badge && (
          <div
            className="flex-shrink-0 text-center rounded-md px-5 py-4 font-extrabold text-white tracking-wide"
            style={{ backgroundColor: "#db0058" }}
          >
            <div className="text-lg">{badge.text}</div>
            {badge.subtext && (
              <div className="text-[10px] font-normal uppercase tracking-widest opacity-85 mt-1">
                {badge.subtext}
              </div>
            )}
          </div>
        )}
        {!badge && HighlightIcon && (
          <span
            className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 rounded-lg"
            style={
              isLight
                ? { backgroundColor: "#db0058" }
                : { backgroundColor: "rgba(219,0,88,0.2)" }
            }
          >
            <HighlightIcon
              className="w-4 h-4 sm:w-5 sm:h-5"
              style={{ color: isLight ? "#ffffff" : "#db0058" }}
            />
          </span>
        )}
        {!badge && !HighlightIcon && stat && (
          <div
            className="flex-shrink-0 text-4xl sm:text-5xl font-extrabold leading-none"
            style={{ color: "#db0058" }}
          >
            {stat}
          </div>
        )}
        <div>
          {title && (
            <h2
              className="text-base sm:text-lg font-bold mb-2"
              style={{ color: isLight ? "#9d174d" : "#ffffff" }}
            >
              {title}
            </h2>
          )}
          {renderParagraphs(
            paragraphs,
            "p",
            isLight
              ? "text-sm sm:text-base text-gray-800 leading-relaxed mb-3"
              : "text-sm sm:text-base text-gray-300 leading-relaxed mb-3",
          )}
          {cta && (
            <div className="mt-4">
              <ServiceCtaButton
                cta={cta}
                variant={isLight ? "primary" : "outline"}
                size="small"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div
        id={id}
        className="scroll-mt-24 bg-gray-50 border border-gray-200 rounded-lg p-5 sm:p-6 h-full"
      >
        {title && (
          <h2 className="flex items-center gap-2 text-sm sm:text-base font-bold text-gray-900 mb-3">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: "#db0058" }}
              aria-hidden="true"
            />
            {title}
          </h2>
        )}
        {renderParagraphs(
          paragraphs,
          "p",
          "text-sm text-gray-700 leading-relaxed mb-3",
        )}
        <ContentList list={list} listStyle={listStyle} />
        {renderParagraphs(
          afterParagraphs,
          "ap",
          "text-sm text-gray-700 leading-relaxed mb-3",
        )}
        {cta && (
          <div className="mt-4">
            <ServiceCtaButton cta={cta} variant="outline" size="small" />
          </div>
        )}
      </div>
    );
  }

  const textBlock = (
    <div className={hasImage ? "" : "max-w-3xl"}>
      {title && (
        <h2
          className={
            titleDivider
              ? "flex items-center gap-3 text-base sm:text-lg font-bold text-gray-900 mb-4 sm:mb-6"
              : "text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6"
          }
        >
          {title}
          {titleDivider && (
            <span className="flex-1 h-px bg-gray-200" aria-hidden="true" />
          )}
        </h2>
      )}

      {renderParagraphs(
        paragraphs,
        "p",
        "text-gray-700 leading-relaxed mb-4 text-base sm:text-lg",
      )}

      <ContentList list={list} listStyle={listStyle} />

      {highlight && (
        <div
          className="rounded-lg border-2 p-5 sm:p-6 mb-4"
          style={{
            backgroundColor: highlight.items ? "#ffffff" : "#fce7f0",
            borderColor: "#db0058",
          }}
        >
          {highlight.heading && (
            <h3
              className="text-sm sm:text-base font-bold mb-2"
              style={{ color: "#be185d" }}
            >
              {highlight.heading}
            </h3>
          )}
          {highlight.content &&
            renderParagraphs(
              Array.isArray(highlight.content)
                ? highlight.content
                : [highlight.content],
              "hl",
              "text-sm sm:text-base leading-relaxed text-gray-800",
            )}
          {Array.isArray(highlight.items) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {highlight.items.map((entry, index) => (
                <div
                  key={entry.heading || index}
                  className="rounded-md p-3 sm:p-4"
                  style={{ backgroundColor: "#fce7f0" }}
                >
                  {entry.heading && (
                    <h4
                      className="text-sm font-bold mb-1"
                      style={{ color: "#be185d" }}
                    >
                      {entry.heading}
                    </h4>
                  )}
                  <p className="text-sm text-gray-800 leading-relaxed">
                    {entry.text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {renderParagraphs(
        afterParagraphs,
        "ap",
        "text-gray-700 leading-relaxed mb-4 text-base sm:text-lg",
      )}

      {cta && (
        <div className="mt-6">
          <ServiceCtaButton cta={cta} variant="outline" size="medium" />
        </div>
      )}
    </div>
  );

  if (!hasImage) {
    return (
      <div id={id} className="scroll-mt-24">
        {textBlock}
      </div>
    );
  }

  return (
    <div
      id={id}
      className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center"
    >
      {/* Text stays first in DOM order regardless of visual position, so reading/tab order is unaffected. */}
      <div className={imagePosition === "left" ? "lg:order-2" : ""}>
        {textBlock}
      </div>
      <div className={imagePosition === "left" ? "lg:order-1" : ""}>
        <img
          src={image}
          alt={imageAlt || ""}
          loading="lazy"
          className="w-full h-auto rounded-lg object-cover"
        />
      </div>
    </div>
  );
};

export default ServiceContentSection;
